import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './SummaryModal.css';

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true
});

Modal.setAppElement('#root');

const escapeHtml = (unsafe) => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const renderFormattedContent = (text, isInline = false) => {
  if (!text) return '';

  const mathBlocks = [];
  const mathInlines = [];

  let processed = text;

  // Extract block equations
  processed = processed.replace(/\$\$\s*([\s\S]*?)\s*\$\$/g, (match, formula) => {
    const placeholder = `MATHBLOCKN${mathBlocks.length}`;
    mathBlocks.push(formula);
    return placeholder;
  });

  // Extract inline equations
  processed = processed.replace(/\$(.*?)\$/g, (match, formula) => {
    const placeholder = `MATHINLINEN${mathInlines.length}`;
    mathInlines.push(formula);
    return placeholder;
  });

  // Parse Markdown using marked
  let html = isInline ? marked.parseInline(processed) : marked.parse(processed);

  // Sanitize to prevent XSS (allowing standard formatting but blocking scripts/malicious handlers)
  html = DOMPurify.sanitize(html);

  // Restore math blocks
  mathBlocks.forEach((formula, idx) => {
    const placeholder = `MATHBLOCKN${idx}`;
    const mathHtml = `<span class="math-block">${escapeHtml(formula)}</span>`;
    html = html.replace(placeholder, mathHtml);
  });

  // Restore math inlines
  mathInlines.forEach((formula, idx) => {
    const placeholder = `MATHINLINEN${idx}`;
    const mathHtml = `<span class="math-inline">${escapeHtml(formula)}</span>`;
    html = html.replace(placeholder, mathHtml);
  });

  return html;
};

const parseSummary = (text) => {
  const defaultSections = {
    overview: '',
    takeaways: [],
    actions: []
  };

  if (!text) return defaultSections;

  const hasHeaders = text.includes('EXECUTIVE OVERVIEW') || text.includes('KEY TAKEAWAYS') || text.includes('ACTION ITEMS');
  
  if (!hasHeaders) {
    defaultSections.overview = text;
    return defaultSections;
  }

  // Use case-insensitive matching to extract markdown sections
  const overviewMatch = text.match(/EXECUTIVE OVERVIEW\s*([\s\S]*?)(?=# KEY TAKEAWAYS|# ACTION ITEMS|$)/i);
  const takeawaysMatch = text.match(/KEY TAKEAWAYS\s*([\s\S]*?)(?=# EXECUTIVE OVERVIEW|# ACTION ITEMS|$)/i);
  const actionsMatch = text.match(/ACTION ITEMS\s*([\s\S]*?)(?=# EXECUTIVE OVERVIEW|# KEY TAKEAWAYS|$)/i);

  const cleanList = (listText) => {
    if (!listText) return [];
    return listText
      .split('\n')
      .map(line => line.replace(/^[\s*•#-]+/, '').trim())
      .filter(Boolean);
  };

  return {
    overview: overviewMatch ? overviewMatch[1].trim() : '',
    takeaways: takeawaysMatch ? cleanList(takeawaysMatch[1]) : [],
    actions: actionsMatch ? cleanList(actionsMatch[1]) : []
  };
};

export const SummaryModal = ({ isOpen, onRequestClose, summary, fileName, onRegenerate, isGenerating }) => {
  const [openSections, setOpenSections] = useState({
    overview: false,
    takeaways: false,
    actions: false
  });

  useEffect(() => {
    if (isOpen) {
      setOpenSections({
        overview: false,
        takeaways: false,
        actions: false
      });
    }
  }, [isOpen]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const parsed = parseSummary(summary);
  const isGeneratingOrWaiting = isGenerating || summary === 'Generating summary...';

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="File Summary Modal"
      className="modal summary-modal-content"
      overlayClassName="overlay"
      style={{ content: { maxWidth: '650px', maxHeight: '85vh', overflowY: 'auto' } }}
    >
      <button className="modal-close-btn" onClick={onRequestClose} aria-label="Close summary" disabled={isGeneratingOrWaiting}>&times;</button>
      <h2>Summary for "{fileName}"</h2>
      
      {isGeneratingOrWaiting ? (
        <div className="summary-loading-state">
          <p style={{ fontWeight: '500', color: 'var(--color-text-secondary)' }}>AI is parsing and summarizing document structure...</p>
          <div className="summary-skeleton-wave" />
          <div className="summary-skeleton-wave" style={{ animationDelay: '0.2s', width: '85%' }} />
          <div className="summary-skeleton-wave" style={{ animationDelay: '0.4s', width: '90%' }} />
        </div>
      ) : (
        <div className="summary-container">
          {/* SECTION 1: EXECUTIVE OVERVIEW */}
          {parsed.overview && (
            <div className={`summary-accordion ${openSections.overview ? 'open' : ''}`}>
              <div className="summary-header" onClick={() => toggleSection('overview')}>
                <div className="summary-title-group">
                  <span className="summary-icon">🎯</span>
                  <h4>Executive Overview</h4>
                </div>
                <span className="summary-arrow">▶</span>
              </div>
              {openSections.overview && (
                <div className="summary-content">
                  <div className="summary-overview-text markdown-body" dangerouslySetInnerHTML={{ __html: renderFormattedContent(parsed.overview) }} />
                </div>
              )}
            </div>
          )}

          {/* SECTION 2: KEY TAKEAWAYS */}
          {parsed.takeaways.length > 0 && (
            <div className={`summary-accordion ${openSections.takeaways ? 'open' : ''}`}>
              <div className="summary-header" onClick={() => toggleSection('takeaways')}>
                <div className="summary-title-group">
                  <span className="summary-icon">💡</span>
                  <h4>Key Takeaways</h4>
                </div>
                <span className="summary-arrow">▶</span>
              </div>
              {openSections.takeaways && (
                <div className="summary-content">
                  <ul className="summary-list">
                    {parsed.takeaways.map((item, idx) => (
                      <li key={idx} className="summary-list-item markdown-body" dangerouslySetInnerHTML={{ __html: renderFormattedContent(item, true) }} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* SECTION 3: ACTION ITEMS */}
          {parsed.actions.length > 0 && (
            <div className={`summary-accordion ${openSections.actions ? 'open' : ''}`}>
              <div className="summary-header" onClick={() => toggleSection('actions')}>
                <div className="summary-title-group">
                  <span className="summary-icon">📋</span>
                  <h4>Action Items</h4>
                </div>
                <span className="summary-arrow">▶</span>
              </div>
              {openSections.actions && (
                <div className="summary-content">
                  <ul className="summary-list">
                    {parsed.actions.map((item, idx) => (
                      <li key={idx} className="summary-list-item markdown-body" dangerouslySetInnerHTML={{ __html: renderFormattedContent(item, true) }} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* REGENERATE TRIGGER */}
          <div className="regenerate-button-wrapper">
            <button 
              className="regenerate-btn" 
              onClick={onRegenerate}
              disabled={isGenerating}
            >
              {isGenerating ? 'Regenerating...' : '🔄 Regenerate with AI'}
            </button>
          </div>
        </div>
      )}

    </Modal>
  );
};